import React, { useEffect, useMemo } from 'react';
import { Calendar } from 'feather-icons-react/build/IconComponents';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector, useDispatch } from 'react-redux';
import { selectVendorData } from '../../feature-module/vendormasters/store/selectors';
import { fetchVendorsWithFilters } from '../../feature-module/vendormasters/store/slice';
import { selectReceivingShipmentFilters, selectReceivingShipmentData } from './store/selectors';
import { actions as receivingActions, fetchReceivingShipmentsWithFilters } from './store/slice';

const { setSupplier, setStatus, setShipmentDate } = receivingActions;

function parseFilterDate(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatFilterDate(date) {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const RECEIVING_STATUS_OPTIONS = [
  { value: 'ASN Received', label: 'ASN Received' },
  { value: 'Partially Received', label: 'Partially Received' },
  { value: 'Printed', label: 'Printed' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Not Started', label: 'Not Started' },
];

const searchableSelectStyles = {
  control: base => ({
    ...base,
    minHeight: 40,
    cursor: 'text',
  }),
  valueContainer: base => ({
    ...base,
    paddingLeft: 8,
    paddingRight: 8,
  }),
  input: base => ({
    ...base,
    height: 'auto',
    margin: 0,
    padding: 0,
    opacity: 1,
  }),
  singleValue: base => ({
    ...base,
    marginLeft: 0,
  }),
  placeholder: base => ({
    ...base,
    marginLeft: 0,
  }),
  menu: base => ({
    ...base,
    zIndex: 5,
  }),
};

const getVendorName = vendor =>
  String(vendor?.vendorName ?? vendor?.VendorName ?? vendor?.company ?? vendor?.COMPANY ?? '').trim();

const MappingsFilter = () => {
  const dispatch = useDispatch();
  const vendors = useSelector(selectVendorData);
  const shipments = useSelector(selectReceivingShipmentData);
  const {
    supplier: selectedSupplier,
    status: selectedStatus,
    shipmentDate,
  } = useSelector(selectReceivingShipmentFilters);

  useEffect(() => {
    if (!Array.isArray(vendors) || vendors.length === 0) {
      dispatch(fetchVendorsWithFilters({}));
    }
  }, [dispatch, vendors]);

  const supplierOptions = useMemo(() => {
    const byName = new Map();
    (Array.isArray(vendors) ? vendors : []).forEach(vendor => {
      const name = getVendorName(vendor);
      if (name) {
        byName.set(name.toLowerCase(), { value: name, label: name, searchText: name });
      }
    });
    (Array.isArray(shipments) ? shipments : []).forEach(shipment => {
      const name = String(shipment?.supplierName || '').trim();
      if (name && !byName.has(name.toLowerCase())) {
        byName.set(name.toLowerCase(), { value: name, label: name, searchText: name });
      }
    });
    return [...byName.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [vendors, shipments]);

  const selectedSupplierOption =
    selectedSupplier && selectedSupplier.value
      ? supplierOptions.find(o => String(o.value) === String(selectedSupplier.value)) ||
        selectedSupplier
      : null;

  const selectedStatusOption =
    selectedStatus && selectedStatus.value
      ? RECEIVING_STATUS_OPTIONS.find(o => o.value === selectedStatus.value) || selectedStatus
      : null;

  const handleSupplierChange = selected => {
    dispatch(setSupplier(selected && selected.value ? selected : null));
  };

  const handleStatusChange = selected => {
    dispatch(setStatus(selected && selected.value ? selected : null));
  };

  const filterOption = (option, rawInput) => {
    const input = String(rawInput || '')
      .trim()
      .toLowerCase();
    if (!input) return true;
    return String(option.label || '')
      .toLowerCase()
      .includes(input);
  };

  const onSearch = e => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(fetchReceivingShipmentsWithFilters());
  };

  return (
    <div className="card visible" id="filter_inputs" style={{ display: 'block' }}>
      <div className="card-body pb-0">
        <div className="row">
          <div className="col-lg-4 col-sm-6 col-12">
            <div className="input-blocks">
              <Select
                className="shipment-filter-select"
                classNamePrefix="react-select"
                options={supplierOptions}
                value={selectedSupplierOption}
                onChange={handleSupplierChange}
                placeholder="Type to search supplier"
                isSearchable
                isClearable
                filterOption={filterOption}
                styles={searchableSelectStyles}
                noOptionsMessage={() => 'No suppliers found'}
              />
            </div>
          </div>
          <div className="col-lg-2 col-sm-6 col-12">
            <div className="input-blocks">
              <Select
                className="shipment-filter-select"
                classNamePrefix="react-select"
                options={RECEIVING_STATUS_OPTIONS}
                value={selectedStatusOption}
                onChange={handleStatusChange}
                placeholder="Type to search status"
                isSearchable
                isClearable
                filterOption={filterOption}
                styles={searchableSelectStyles}
                noOptionsMessage={() => 'No statuses found'}
              />
            </div>
          </div>
          <div className="col-lg-2 col-sm-6 col-12">
            <div className="input-blocks">
              <Calendar className="info-img" />
              <DatePicker
                selected={parseFilterDate(shipmentDate)}
                onChange={date => dispatch(setShipmentDate(formatFilterDate(date)))}
                dateFormat="MM/dd/yyyy"
                className="form-control"
                placeholderText="MM/DD/YYYY"
                autoComplete="off"
                isClearable
              />
            </div>
          </div>
          <div className="col-lg-4 col-sm-6 col-12 ms-auto">
            <div className="input-blocks">
              <button type="button" className="btn btn-filters ms-auto" onClick={onSearch}>
                <i data-feather="search" className="feather-search" /> Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MappingsFilter;
